import React, { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, ChevronDown, ChevronRight, History, RefreshCw } from 'lucide-react';
import { Button } from '@components/Button';
import { CertificateLogEntry } from '@/types/certificate';

interface Props {
  /**
   * Busca a trilha. Recebe o serviço já resolvido pela tela — é o que faz este
   * componente servir tanto a configuração PIX quanto a conta corrente, cujos
   * endpoints são diferentes mas cujos passos são os mesmos.
   */
  fetchLogs: () => Promise<CertificateLogEntry[]>;
  /** Muda a cada operação concluída, para o histórico recarregar sozinho. */
  refreshKey?: number;
  /** Texto do "nenhuma operação registrada" — muda o sujeito por tela. */
  emptyLabel?: string;
}

/**
 * Histórico das operações de certificado.
 *
 * Renovar é uma conversa com o STS do Itaú que acontece uma vez por ano por
 * credencial e não pode ser repetida à vontade — cada tentativa consome a
 * janela de 30 dias. Quando algo dá errado, a pergunta é sempre "o que
 * exatamente foi enviado e o que exatamente voltou"; até aqui a resposta só
 * existia no log do pod, que rotaciona.
 *
 * Cada linha é um passo, com o comando equivalente para reproduzir fora do
 * sistema. Os passos de uma mesma execução ficam agrupados.
 */
export const CertificateLog: React.FC<Props> = ({ fetchLogs, refreshKey, emptyLabel }) => {
  const [entries, setEntries] = useState<CertificateLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      setEntries(await fetchLogs());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar o histórico');
    } finally {
      setLoading(false);
    }
  };

  // Só busca quando a seção é aberta — o histórico é consulta pontual, não vale
  // uma requisição a cada abertura do formulário.
  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, refreshKey]);

  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const groups = groupByExecution(entries);

  return (
    <div className="mt-6 pt-6 border-t border-slate-200">
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-2 text-sm font-medium text-slate-800 hover:text-slate-900"
        >
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <History size={16} />
          Histórico das operações de certificado
        </button>
        {open && (
          <Button size="sm" variant="secondary" onClick={load} loading={loading} icon={<RefreshCw size={14} />}>
            Atualizar
          </Button>
        )}
      </div>

      {open && (
        <div className="mt-3 space-y-4">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {!loading && !error && groups.length === 0 && (
            <p className="text-sm text-slate-500">
              {emptyLabel ?? 'Nenhuma operação registrada ainda.'}
            </p>
          )}

          {groups.map((group) => (
            <ExecutionGroup
              key={group.correlationId}
              group={group}
              expanded={expanded}
              onToggle={toggle}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface ExecutionGroup {
  correlationId: string;
  operation: string;
  startedAt: string;
  triggeredBy?: string;
  failed: boolean;
  steps: CertificateLogEntry[];
}

/**
 * Agrupa os passos por execução, preservando a ordem em que aconteceram.
 *
 * O backend devolve do mais recente para o mais antigo (é o que a tela quer:
 * a última execução primeiro), mas DENTRO de uma execução a ordem útil é a
 * cronológica — ler "token, renovação, gravação" ao contrário não conta a
 * história.
 */
const groupByExecution = (entries: CertificateLogEntry[]): ExecutionGroup[] => {
  const byCorrelation = new Map<string, CertificateLogEntry[]>();
  for (const e of entries) {
    const list = byCorrelation.get(e.correlation_id);
    if (list) list.push(e);
    else byCorrelation.set(e.correlation_id, [e]);
  }

  return Array.from(byCorrelation.entries()).map(([correlationId, steps]) => {
    const ordered = [...steps].sort((a, b) => a.sequence - b.sequence);
    const first = ordered[0];
    return {
      correlationId,
      operation: first.operation,
      startedAt: first.created_at,
      triggeredBy: first.triggered_by,
      failed: ordered.some((s) => !s.success),
      steps: ordered,
    };
  });
};

const OPERATION_LABEL: Record<string, string> = {
  upload: 'Envio de certificado',
  test: 'Teste de certificado',
  renew: 'Renovação de certificado',
};

const STEP_LABEL: Record<string, string> = {
  inspect_pfx: 'Abrir e conferir o .pfx',
  check_cn: 'Conferir o CN contra o Client ID',
  load_certificate: 'Baixar o certificado do Storage',
  generate_csr: 'Gerar chave e CSR',
  token: 'Autenticar no Itaú (token)',
  renew: 'Renovar no Itaú',
  build_pfx: 'Empacotar o .pfx',
  archive_renewal: 'Arquivar a cópia de auditoria',
  upload_blob: 'Enviar o .pfx para o Storage',
  update_db: 'Gravar na configuração',
  hub_reload: 'Avisar o PaymentHub',
  hub_status: 'Conferir o certificado no PaymentHub',
};

const ExecutionGroup: React.FC<{
  group: ExecutionGroup;
  expanded: Set<number>;
  onToggle: (id: number) => void;
}> = ({ group, expanded, onToggle }) => (
  <div className="rounded-lg border border-slate-200">
    <div
      className={`flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-slate-200 ${
        group.failed ? 'bg-red-50' : 'bg-slate-50'
      }`}
    >
      <div className="flex items-center gap-2">
        {group.failed ? (
          <XCircle size={16} className="text-red-600" />
        ) : (
          <CheckCircle2 size={16} className="text-emerald-600" />
        )}
        <span className="text-sm font-medium text-slate-800">
          {OPERATION_LABEL[group.operation] ?? group.operation}
        </span>
        <span className="text-xs text-slate-500">{group.startedAt}</span>
      </div>
      <span className="text-xs text-slate-500">
        {group.steps.length} passo(s)
        {group.triggeredBy ? ` · ${group.triggeredBy}` : ''}
      </span>
    </div>

    <ul className="divide-y divide-slate-100">
      {group.steps.map((step) => (
        <StepRow
          key={step.id}
          step={step}
          open={expanded.has(step.id)}
          onToggle={() => onToggle(step.id)}
        />
      ))}
    </ul>
  </div>
);

const StepRow: React.FC<{ step: CertificateLogEntry; open: boolean; onToggle: () => void }> = ({
  step,
  open,
  onToggle,
}) => (
  <li>
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50"
    >
      {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      {step.success ? (
        <CheckCircle2 size={14} className="text-emerald-600 flex-shrink-0" />
      ) : (
        <XCircle size={14} className="text-red-600 flex-shrink-0" />
      )}
      <span className="text-sm text-slate-800">{STEP_LABEL[step.step] ?? step.step}</span>
      {step.response_status ? (
        <span
          className={`text-xs font-mono ${
            step.response_status >= 400 ? 'text-red-600' : 'text-slate-500'
          }`}
        >
          HTTP {step.response_status}
        </span>
      ) : null}
      {step.duration_ms ? <span className="text-xs text-slate-400">{step.duration_ms} ms</span> : null}
    </button>

    {open && (
      <div className="px-3 pb-3 space-y-2">
        {step.error_message && (
          <Field label="Erro" value={step.error_message} tone="error" />
        )}
        {step.command && <Field label="Comando equivalente" value={step.command} mono />}
        {step.request_url && (
          <Field label="Requisição" value={`${step.request_method ?? ''} ${step.request_url}`.trim()} mono />
        )}
        {step.request_headers && <Field label="Headers enviados" value={step.request_headers} mono />}
        {step.request_body && <Field label="Corpo / detalhe" value={step.request_body} mono />}
        {step.response_headers && <Field label="Headers da resposta" value={step.response_headers} mono />}
        {step.response_body && <Field label="Corpo da resposta" value={step.response_body} mono />}
        {step.itau_correlation_id && (
          <Field
            label="Protocolo Itaú (x-itau-correlationID)"
            value={step.itau_correlation_id}
            mono
          />
        )}
      </div>
    )}
  </li>
);

const Field: React.FC<{ label: string; value: string; mono?: boolean; tone?: 'error' }> = ({
  label,
  value,
  mono,
  tone,
}) => (
  <div>
    <p className="text-xs font-medium text-slate-500 mb-1">{label}</p>
    {/* overflow-x-auto: um PEM ou um header longo não pode empurrar a página
        inteira para o lado. */}
    <pre
      className={`text-xs whitespace-pre-wrap break-all rounded border p-2 overflow-x-auto ${
        tone === 'error' ? 'border-red-200 bg-red-50 text-red-700' : 'border-slate-200 bg-slate-50 text-slate-700'
      } ${mono ? 'font-mono' : ''}`}
    >
      {value}
    </pre>
  </div>
);
